import type { LocaleParams } from '@lotta/self-service/lottery-fe/types-router';
import { getPathWithLocale } from '@lotta/self-service/lottery-fe/util-utilities';
import { useCallback } from 'react';
import type { NavigateFunction, NavigateOptions, To } from 'react-router-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

/**
 * Hook which returns a decorated navigate function to add country code and language to beginning of path.
 *
 * Example: '/test' => '/{countryCode}/{language}/test'
 */
export const useNavigateWithLocale = (): NavigateFunction => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { countryCode, language } = useParams<LocaleParams>() as LocaleParams;

  const navigateFunction: NavigateFunction = useCallback(
    (to: To | number, options: NavigateOptions = {}) => {
      if (typeof to === 'number') {
        navigate(to);
        return;
      }

      const path = getPathWithLocale(to, pathname, countryCode, language);

      navigate(path, options);
    },
    [navigate, countryCode, language, pathname]
  );

  return navigateFunction;
};
